import { useCallback, useMemo } from 'react';

import type {
  UpdateAssessmentBookmarkMutation,
  UpdateAssessmentBookmarkMutationVariables,
} from '../../assessment/assessment.graphql.generated';
import { UPDATE_ASSESSMENT_BOOKMARKS } from 'approot/assessment/assessment.graphql';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useCurrentClassVar } from 'approot/classes/classes.hooks';
import { removeTypenameFromArray } from 'approot/shared/logic/remove-typename';
import type { LessonAssignFragment } from '../assign/assign-lesson.graphql.generated';
import type { Pluck } from 'lib/gql/pluck';

export const useBookmarkAssessmentActivities = ({
  assessmentId,
  unitId,
  assignedData,
  onCompleted,
}: {
  assessmentId: number;
  unitId: number;
  assignedData: {
    stimulusResourceIds: number[];
    selfAssessment?: boolean | null;
    differentiationPages?: number[];
    basicAssessmentPages?: number[];
    summativeAssessmentPages?: number[];
    formativeAssessmentPages?: number[];
    activitiesBookmarks?: {
      __typename: 'ActivitiesBookmarkMetaData';
      version: string;
      sectionIds: string[];
    };
  } | null;
  onCompleted?: (
    assignedData: Pluck<
      UpdateAssessmentBookmarkMutation,
      'updateAssessmentBookmark.assignedData'
    > | null,
    bookmarked: boolean
  ) => void;
}) => {
  const { showError } = useSnackbar();

  const currentClass = useCurrentClassVar();
  const classId = currentClass?.id || 0;

  const [updateAssessmentBookmarks, { loading }] = useLoggingMutation<
    UpdateAssessmentBookmarkMutation,
    UpdateAssessmentBookmarkMutationVariables
  >(UPDATE_ASSESSMENT_BOOKMARKS, {
    onCompleted: res =>
      onCompleted?.(
        res.updateAssessmentBookmark.assignedData,
        res.updateAssessmentBookmark.isAssigned
      ),
  });

  const diffPagesBookmarked = useMemo(
    () => assignedData?.differentiationPages || [],
    [assignedData]
  );

  const selfAssessmentBookmarked = !!(
    assignedData && assignedData.selfAssessment
  );

  const basicAssessmentPagesBookmarked = useMemo(
    () => assignedData?.basicAssessmentPages || [],
    [assignedData]
  );

  const summativeAssessmentPagesBookmarked = useMemo(
    () => assignedData?.summativeAssessmentPages || [],
    [assignedData]
  );

  const formativeAssessmentPagesBookmarked = useMemo(
    () => assignedData?.formativeAssessmentPages || [],
    [assignedData]
  );

  const handleBookmarkResources = useCallback(
    ({
      activitiesBookmarks,
      stimulusResourceIds,
    }: {
      activitiesBookmarks: Pluck<
        LessonAssignFragment,
        'assignedData.activitiesBookmarks'
      >[];
      stimulusResourceIds: number[];
    }) => {
      const bookmarkSelfAssessment = selfAssessmentBookmarked;
      const diffPages = diffPagesBookmarked;

      const basicAssessmentPages = basicAssessmentPagesBookmarked;

      const summativeAssessmentPages = summativeAssessmentPagesBookmarked;

      const formativeAssessmentPages = formativeAssessmentPagesBookmarked;

      return updateAssessmentBookmarks({
        variables: {
          input: {
            assessmentId,
            unitId,
            selfAssessment: bookmarkSelfAssessment,
            differentiationPages: diffPages,
            basicAssessmentPages,
            summativeAssessmentPages,
            formativeAssessmentPages,
            stimulusResourceIds,
            activitiesBookmarks: removeTypenameFromArray(
              activitiesBookmarks || []
            ),
            classId,
          },
        },
      }).catch(() => {
        showError('There was an error updating the bookmarks.');
      });
    },
    [
      selfAssessmentBookmarked,
      diffPagesBookmarked,
      basicAssessmentPagesBookmarked,
      summativeAssessmentPagesBookmarked,
      formativeAssessmentPagesBookmarked,
      assessmentId,
      unitId,
      classId,
      updateAssessmentBookmarks,
      showError,
    ]
  );

  return {
    loading,
    handleBookmarkResources,
  };
};
